/**
 * Catalyst Auto-Assignment Utility
 * 
 * For Catalyst/Enterprise tier shops, this utility calls smart-assign
 * and automatically selects the top-ranked technician with a brief
 * explanation for toast notifications.
 */

import { supabase } from '@/integrations/supabase/client';
import { format } from 'date-fns';
import type { RankingRolloverPart, RankingRolloverPlan } from '@/utils/rolloverPlanClient';
import { readRankingRecord, type RankingRecord } from '@/lib/assignment/rankingAuditRecord';

export interface TechnicianRanking {
  technicianId: string;
  technicianName: string;
  score: number;
  reasoning: string;
  proficiencyMatch: number;
  availabilityScore: number;
  workloadScore: number;
  bayMatch: boolean;
  optiTime: number;
  suggestedStartTime: string | null;
  suggestedFinishTime: string | null;
  willRollover?: boolean;
  /** The multi-day plan: Part A, B, C, … — any number of parts. */
  rolloverParts?: RankingRolloverPart[];
  /** Whole-chain completion; never a part's own OptiFinish. */
  chainFinishTime?: string | null;
  /** The workdays the server resolved, for re-planning a moved start. */
  rolloverPlan?: RankingRolloverPlan | null;
}

export interface AutoAssignResult {
  success: boolean;
  topPick: TechnicianRanking | null;
  allRankings: TechnicianRanking[];
  allRawRankings: any[]; // Includes techs filtered out as ineligible
  /** Core-100 R5 (CORE-AB S): the ranking run smart-assign recorded (attach it with record_assignment_selection). */
  rankingRecord?: RankingRecord | null;
  reason: string;
  noAvailability: boolean;
  splitEligible: boolean;
}

export interface AutoAssignTicketData {
  /** Core-100 R4 CORE-Y: how the ticket originated (tickets.creation_method). Absent = typed. */
  creationMethod?: 'create_ticket' | 'document_upload' | 'conversation';
  /** Core-100 R5 (CORE-AQ): the call proposal this new ticket is created from (tickets.source_call_proposal_id). */
  sourceCallProposalId?: string;
  shop_id: string;
  /** The selected existing vehicle (returning customer) — reused, never duplicated. */
  vehicleId?: string | null;
  vehicle?: {
    year: number;
    make: string;
    model: string;
    engineSize?: string;
    vin?: string;
    licensePlate?: string;
  };
  customerName?: string;
  customerPhone?: string;
  type: string;
  priority: string;
  isComeback: boolean;
  appointmentDate: string;
  dueOut?: string;
  startTime?: string;
  /** Parking-lot spot id when the vehicle arrives parked (parked-on-arrival). */
  parkingSpotId?: string | null;
  jobs: Array<{
    category: string;
    submenu: string;
    name: string;
    minutes: number;
    bay: string;
    skill: number;
    proficiency_category?: string;
    notes?: string;
  }>;
  predictedMinutes: number;
}

/**
 * Build a concise reason string for the toast notification.
 *
 * Chronomancer R-OPT-1: the reasoning must match the actual ranking logic.
 * The server-side smart-assign uses a lexicographic sort with the strict
 * priority: (1) proficiency band, (2) current workload (lower wins),
 * (3) EFS / earliest start time, (4) historical affinity, (5) fallback
 * display score. Previously the reason builder keyed off
 * `availabilityScore >= 90` and `workloadScore >= 85`, neither of which
 * are ranking keys — the toast said "Earliest availability" or
 * "Balanced workload" even though those factors only applied when
 * proficiency tied. Leading with those factors misled advisors about
 * why a tech was chosen: a tech picked purely for proficiency would see
 * a "Balanced workload" explanation because their workload score
 * happened to be >= 85, even though workload never entered the sort.
 *
 * The new logic expresses the actual ranking priority:
 *   - Proficiency is KING; the primary reason is always the proficiency
 *     match percentage plus the start time.
 *   - Workload, earliest-start, and affinity are secondary context —
 *     included only to enrich the message, never leading.
 *
 * Server-side smart-assign also builds a richer `deterministicReasoning`
 * (index.ts:1928+) when available; prefer it when we have it so the
 * toast and the detailed ranking panel stay aligned.
 */
export function buildAssignmentReason(ranking: TechnicianRanking): string {
  const startTime = ranking.suggestedStartTime
    ? format(new Date(ranking.suggestedStartTime), 'h:mm a')
    : 'ASAP';

  const profMatch = Math.round(ranking.proficiencyMatch);

  // Prefer the server's deterministic reasoning when available — it's
  // built from the same factors that drove the sort and always leads
  // with proficiency.
  const serverReason = (ranking as any).reasoning;
  if (typeof serverReason === 'string' && serverReason.trim().length > 0) {
    return serverReason.length > 140
      ? serverReason.slice(0, 137).trimEnd() + '…'
      : serverReason;
  }

  // Fallback: proficiency-first template. Never lead with availability
  // or workload — they're tie-breakers, not primary ranking criteria.
  if (profMatch >= 95) {
    return `Best proficiency match (${profMatch}%), starts ${startTime}`;
  }
  return `${profMatch}% proficiency match, starts ${startTime}`;
}

/**
 * Call smart-assign and return the top-ranked technician with reasoning
 */
export async function autoSmartAssign(
  ticketData: AutoAssignTicketData,
  selectedDate: Date
): Promise<AutoAssignResult> {
  try {
    const isWaiter = String(ticketData.type || '').toLowerCase() === 'waiter';
    const fixedStartTime = isWaiter && ticketData.startTime ? ticketData.startTime : null;
    
    const { data, error } = await supabase.functions.invoke('smart-assign', {
      body: {
        ticketData: {
          ...ticketData,
          appointmentDate: format(selectedDate, 'yyyy-MM-dd'),
          isWaiter,
          fixedStartTime,
        },
        // Core-100 R5 (CORE-AB S): the server records this ranking as a draft run for the ticket about to be created.
        recordRanking: true,
      }
    });

    if (error) {
      console.error('[AUTO-ASSIGN] smart-assign error:', error);
      return {
        success: false,
        topPick: null,
        allRankings: [],
        allRawRankings: [],
        reason: error.message || 'Failed to analyze technicians',
        noAvailability: true,
        splitEligible: false,
      };
    }

    // Check for split eligibility - fall back to modal for MVP
    if (data?.splitSuggestion?.eligible) {
      console.log('[AUTO-ASSIGN] Split eligible, falling back to modal');
      return {
        success: false,
        topPick: null,
        allRankings: data.rankings || [],
        allRawRankings: data.auditData || data.rankings || [],
        reason: 'This ticket is eligible for splitting',
        noAvailability: false,
        splitEligible: true,
      };
    }

    // Filter valid rankings (must have concrete times or rollover flag)
    const validRankings = (data.rankings || []).filter((r: TechnicianRanking) =>
      typeof r.score === 'number' &&
      !Number.isNaN(r.score) &&
      (
        (r.suggestedStartTime && r.suggestedFinishTime) ||
        r.willRollover
      )
    );

    if (validRankings.length === 0) {
      return {
        success: false,
        topPick: null,
        allRankings: [],
        allRawRankings: data.rankings || [],
        reason: 'No technician availability found',
        noAvailability: true,
        splitEligible: false,
      };
    }

    // Chronomancer R-OPT-5: trust the server's canonical lexicographic
    // order. The server already sorts rankings by proficiency band →
    // workload → EFS → historical affinity → display score, then anchors
    // display scores post-hoc to monotonically decrease with rank. A
    // client-side re-sort by `score` duplicates the server's final
    // tie-breaker while silently discarding the upstream keys. In the
    // typical case the two orderings agree, but an anchor edge case
    // (e.g. all techs clamped to the same 60 floor) could reorder. The
    // server's order is canonical; just preserve it.
    const topPick = validRankings[0];
    const reason = buildAssignmentReason(topPick);

    console.log('[AUTO-ASSIGN] Top pick:', {
      name: topPick.technicianName,
      score: topPick.score,
      proficiency: topPick.proficiencyMatch,
      startTime: topPick.suggestedStartTime,
      reason,
    });

    return {
      success: true,
      topPick,
      allRankings: validRankings,
      allRawRankings: data.auditData || data.rankings || [],
      rankingRecord: readRankingRecord(data),
      reason,
      noAvailability: false,
      splitEligible: false,
    };
  } catch (err: any) {
    console.error('[AUTO-ASSIGN] Error:', err);
      return {
        success: false,
        topPick: null,
        allRankings: [],
        allRawRankings: [],
        reason: err.message || 'Unexpected error during assignment',
        noAvailability: true,
        splitEligible: false,
      };
  }
}

/**
 * Check if shop tier is Catalyst or Enterprise
 */
export function isCatalystTier(tier: string | null | undefined): boolean {
  const t = (tier || '').toUpperCase();
  return t === 'CATALYST' || t === 'ENTERPRISE';
}
